import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Sunrise } from "lucide-react";
import { Card } from "@/components/ranger/PageHeader";
import { BTN_SECONDARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const STATUS = { open: "text-sky-300", passed: "text-emerald-400", failed: "text-slate-500" };

const useList = (cid, path) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`/communities/${cid}/${path}`).then((r) => setRows(r.data)).catch(() => {}), [cid, path]);
  useEffect(() => { load(); }, [load]);
  return [rows, load];
};

export const DayVotes = ({ cid }) => {
  const { can } = useAuth();
  const [rows, load] = useList(cid, "day-votes");
  const start = async () => { try { const { data } = await api.post(`/communities/${cid}/day-votes/start`); toast.success(`Vote started: ${data.needed} vote(s) needed`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Day votes" icon={Sunrise} testId="day-votes-card" right={can("moderator") && <button data-testid="day-vote-start" onClick={start} className={BTN_SECONDARY}>Start vote now</button>}>
      {rows.length === 0 ? <p className="text-sm text-slate-500">No votes yet.</p> : (
        <ul data-testid="day-votes-list" className="max-h-72 divide-y divide-[#1E2D4A] overflow-y-auto">{rows.map((v) => (
          <li key={v.id} className="flex items-center gap-3 py-2 font-mono text-xs">
            <span className="text-slate-500">{fmtTime(v.created_at)}</span>
            <span className={`uppercase ${STATUS[v.status] || "text-slate-400"}`}>{v.status}</span>
            <span className="text-slate-300">{v.votes}/{v.needed} votes</span>
            <span className="ml-auto truncate text-slate-500" title={v.result}>{v.by}{v.result ? ` · ${v.result}` : ""}</span>
          </li>
        ))}</ul>
      )}
    </Card>
  );
};

export const Kings = ({ cid }) => {
  const { can } = useAuth();
  const [rows, load] = useList(cid, "kings");
  const run = async () => { try { const { data } = await api.post(`/communities/${cid}/kings/run`); toast.success(data.king ? `${data.king} crowned` : "No kills today, nobody crowned"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Kings of the Day" icon={Crown} testId="kings-card" right={can("admin") && <button data-testid="king-run" onClick={run} className={BTN_SECONDARY}>Crown now</button>}>
      {rows.length === 0 ? <p className="text-sm text-slate-500">Crowned automatically every night at midnight (US Central).</p> : (
        <ul data-testid="kings-list" className="max-h-72 divide-y divide-[#1E2D4A] overflow-y-auto">{rows.map((k) => (
          <li key={k.id} className="flex items-center gap-3 py-2 text-xs">
            <span className="font-mono text-slate-500">{k.day}</span>
            <span className="font-semibold text-amber-300">{k.king || "—"}</span>
            <span className="font-mono text-slate-300">{k.kills} kills</span>
            <span className="ml-auto font-mono text-slate-500">{k.reward ? `+${k.reward}` : ""} {k.posted && k.posted !== "sent" ? "· post failed" : ""}</span>
          </li>
        ))}</ul>
      )}
    </Card>
  );
};
